import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { ProviderCard } from "@/components/search/ProviderCard";
import {
 specialtySlug,
 resolveSpecialtyFromSlug,
 SEO_INSURANCE_IDS,
 stateFromNameSlug,
 stateNameSlug,
} from "@/lib/seo-slugs";
import { INSURANCES, searchProviders, type Provider } from "@/data/heallexa";
import { buildProviderSlug } from "@/lib/provider-slug";
import { upsertLandingPage } from "@/lib/seo-pages.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialty/$specialty/doctors/$state")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setSeoPageCache();
 const state = stateFromNameSlug(params.state);
 if (!state) throw notFound();
 const specialty = resolveSpecialtyFromSlug(params.specialty);
 let providers: Provider[] = [];
 try {
 providers = await searchProviders({
 specialty,
 state: state.code,
 limit: 25,
 });
 } catch {
 providers = [];
 }
 const slug = `/specialty/${specialtySlug(specialty)}/doctors/${stateNameSlug(state.name)}`;
 const countLabel = providers.length > 0 ? `${providers.length}+` : "";
 const baseTitle = countLabel
  ? `${countLabel} ${specialty} in ${state.name} — Book Online`
  : `${specialty} in ${state.name}`;
 const title = `${baseTitle} | Heallexa`;
 const description = providers.length > 0
  ? `Find ${providers.length}+ verified ${specialty.toLowerCase()} in ${state.name}. Compare credentials, check insurance accepted, and book online on Heallexa.`
  : `Find top-rated ${specialty.toLowerCase()} across ${state.name}. Compare verified providers, read reviews, check insurance accepted on Heallexa.`;
 try {
 await upsertLandingPage({
 data: {
 page_type: "specialty_state",
 slug,
 specialty,
 state: state.code,
 provider_count: providers.length,
 title,
 description,
 },
 });
 } catch {
 /* non-fatal */
 }
 return { specialty, state, providers, title, description, slug };
 },
 head: ({ loaderData }) => {
 const title = loaderData?.title ?? "Heallexa";
 const description = loaderData?.description ?? "";
 const url = `${SITE}${loaderData?.slug ?? ""}`;
 const providers = loaderData?.providers ?? [];
 const specialty = loaderData?.specialty ?? "";
 const state = loaderData?.state;
 const noindex = providers.length === 0;
 const meta: Array<Record<string, string>> = [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ];
 if (noindex) meta.push({ name: "robots", content: "noindex, follow" });

 const specSlug = specialty.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

 const scripts = [];

 scripts.push({
  type: "application/ld+json",
  children: JSON.stringify({
   "@context": "https://schema.org",
   "@type": "CollectionPage",
   name: title,
   description,
   url,
   numberOfItems: providers.length,
   about: { "@type": "MedicalSpecialty", name: specialty },
   areaServed: state ? { "@type": "State", name: state.name, addressCountry: "US" } : undefined,
   isPartOf: { "@id": `${SITE}/#website` },
  }),
 });

 scripts.push({
  type: "application/ld+json",
  children: JSON.stringify({
   "@context": "https://schema.org",
   "@type": "BreadcrumbList",
   itemListElement: [
   { "@type": "ListItem", position: 1, name: "Home", item: SITE },
   { "@type": "ListItem", position: 2, name: "Specialties", item: `${SITE}/specialties` },
   { "@type": "ListItem", position: 3, name: specialty, item: `${SITE}/specialties/${specSlug}` },
   ...(state ? [{ "@type": "ListItem", position: 4, name: `${specialty} in ${state.name}`, item: url }] : []),
   ],
  }),
 });

 if (providers.length > 0) {
  scripts.push({
  type: "application/ld+json",
  children: JSON.stringify({
   "@context": "https://schema.org",
   "@type": "ItemList",
   numberOfItems: providers.length,
   itemListElement: providers.slice(0, 25).map((p, i) => ({
   "@type": "ListItem",
   position: i + 1,
   url: `${SITE}/providers/${buildProviderSlug(p)}`,
   name: p.name,
   })),
  }),
  });
 }

 return {
 meta,
 links: [{ rel: "canonical", href: url }],
 scripts,
 };
 },
 component: SpecialtyStatePage,
});

function SpecialtyStatePage() {
 const { specialty, state, providers } = Route.useLoaderData();
 const stateSlug = stateNameSlug(state.name);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary transition-colors">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/specialties" className="hover:text-primary transition-colors">Specialties</a>
 <span className="mx-2">/</span>
 <a href={`/locations/${stateSlug}`} className="hover:text-primary transition-colors">
 {state.name}
 </a>
 <span className="mx-2">/</span>
 <span className="text-foreground">{specialty}</span>
 </nav>

 <header className="mb-8">
 <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
 {state.name} · {specialty}
 </p>
 <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">
 {specialty} in {state.name}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-2xl">
 {providers.length > 0
 ? `${providers.length}+ verified ${specialty.toLowerCase()} across ${state.name}. Compare, check insurance, and book on Heallexa, free, no signup.`
 : `We're still building our ${specialty.toLowerCase()} listings in ${state.name}. Browse nearby cities or check the specialty directory.`}
 </p>
 <div className="mt-5 flex flex-wrap gap-3">
 <a
 href={`/specialties/${specialtySlug(specialty)}`}
 className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
 >
 Browse all {specialty} →
 </a>
 </div>
 </header>

 {providers.length > 0 ? (
 <section className="mb-16">
 <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground mb-8">
 Top {specialty.toLowerCase()} in {state.name}
 </h2>
 <ul className="grid gap-5">
 {providers.slice(0, 24).map((p: any) => (
 <li key={p.id}><ProviderCard p={p} /></li>
 ))}
 </ul>
 </section>
 ) : null}

 <Reveal from="up" delay={60}>
 <section className="mb-16">
 <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground mb-8">
 {specialty} by insurance in {state.name}
 </h2>
 <ul className="flex flex-wrap gap-2">
 {SEO_INSURANCE_IDS.map((id) => {
 const ins = INSURANCES.find((i) => i.id === id);
 if (!ins) return null;
 return (
 <li key={id}>
 <Link
 to="/insurance/$slug"
 params={{ slug: id }}
 className="inline-flex items-center h-9 px-3 text-[13px] rounded-md border border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
 >
 {ins.short} · {specialty}
 </Link>
 </li>
 );
 })}
 </ul>
 </section>
 </Reveal>
 </main>
 <FooterSection />
 </div>
 );
}
